import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { motion } from "framer-motion";
import { Search, Signal, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function ManagerAccounts() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["admin_stats_accounts"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("admin-stats", {
        body: { section: "accounts" },
      });
      if (error) throw error;
      return data?.accounts || [];
    },
  });

  const accounts = (data || []).filter(
    (a: any) =>
      a.email.toLowerCase().includes(search.toLowerCase()) ||
      a.id.includes(search)
  );

  const statusLabel = (s: string | null) => {
    switch (s) {
      case "active": return <Badge className="bg-accent/20 text-accent border-0">Ativo</Badge>;
      case "trialing": return <Badge className="bg-blue-500/20 text-blue-400 border-0">Trial</Badge>;
      case "canceled": return <Badge className="bg-destructive/20 text-destructive border-0">Cancelado</Badge>;
      case "past_due": return <Badge className="bg-yellow-500/20 text-yellow-400 border-0">Em atraso</Badge>;
      default: return <Badge variant="outline" className="text-muted-foreground">Free</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-foreground">Contas</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Todas as contas cadastradas na plataforma
        </p>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar por e-mail..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 bg-secondary/40 border-warmz-glass-border"
        />
      </div>

      <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
        <Card className="glass-card border-warmz-glass-border/50">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent border-warmz-glass-border/30">
                    <TableHead>E-mail</TableHead>
                    <TableHead>Plano</TableHead>
                    <TableHead>Portas</TableHead>
                    <TableHead>Instâncias</TableHead>
                    <TableHead>Criado em</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <TableRow key={i}>
                        {Array.from({ length: 5 }).map((_, j) => (
                          <TableCell key={j}><Skeleton className="h-5 w-24" /></TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : accounts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-12 text-muted-foreground">
                        Nenhuma conta encontrada.
                      </TableCell>
                    </TableRow>
                  ) : (
                    accounts.map((a: any) => (
                      <TableRow
                        key={a.id}
                        className="cursor-pointer hover:bg-warmz-card-hover/40 transition-colors"
                        onClick={() => navigate(`/manager/accounts/${a.id}`)}
                      >
                        <TableCell>
                          <span className="text-sm font-medium text-foreground">{a.email}</span>
                        </TableCell>
                        <TableCell>{statusLabel(a.subscription_status)}</TableCell>
                        <TableCell>
                          <span className="text-sm text-foreground">{a.max_ports}</span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            <Signal className="h-3 w-3 text-accent" />
                            <span className="text-sm">{a.instances_online}/{a.instances_total}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-xs text-muted-foreground">
                            {new Date(a.created_at).toLocaleDateString("pt-BR")}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
